import { useEffect, useRef } from "react";

import { getUser } from "../utils";

const IDENTITY_KEYS = new Set(["user", "token"]);

/** True when the active user id changed from what this tab booted with
 *  (incl. becoming null on logout) — i.e. another tab switched accounts. */
export function shouldReloadOnIdentityChange(
  bootUserId: string | null,
  currentUserId: string | null
): boolean {
  return bootUserId !== currentUserId;
}

/**
 * Keeps an open tab honest about which account it's showing. `localStorage` is
 * shared across tabs (last-writer-wins), so logging into another account in a
 * second tab silently swaps this tab's stored identity while its React tree
 * keeps rendering the old account. Listen for that cross-tab change and reload
 * so every identity source (localStorage reads, context, memoized hooks) resets
 * together. Mount once at the app root.
 */
export function useCrossTabIdentitySync(): void {
  const bootUserId = useRef<string | null>(null);

  useEffect(() => {
    bootUserId.current = getUser()?.id ?? null;

    const onStorage = (e: StorageEvent) => {
      // key is null when another tab calls localStorage.clear() (logout).
      if (e.key !== null && !IDENTITY_KEYS.has(e.key)) return;
      const currentUserId = getUser()?.id ?? null;
      if (shouldReloadOnIdentityChange(bootUserId.current, currentUserId)) {
        window.location.reload();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
}
